/**
 * The global chrome, and the cross-cutting UI patterns.
 *
 * UF_00 section 3.3 states the core principle that shapes every one of these:
 *
 *   "Compliance is visible, not hidden. Tier, caps, escrow state, and sandbox status are always
 *    legible."
 *
 * And: "Blocked actions self-explain with a disclosable reason and always offer a next step."
 *
 * A bare "transaction failed" is a consumer protection failure, not merely a poor error message. So
 * every one of these components is built once, here, and reused, which is what makes the vocabulary
 * consistent: the user, the operator, and the regulator read the same words for the same state.
 */

import type { Tier } from '@/lib/domain';

/**
 * The sandbox banner.
 *
 * Always present. It can be collapsed to a slim strip, and it can NEVER be fully hidden (UF_00).
 * A participant must always be able to see that they are in an experimental, CMA-supervised
 * environment where the protections differ from a fully licensed service. Letting them dismiss that
 * permanently would defeat the disclosure it exists to make.
 */
export function SandboxBanner() {
  return (
    <div className="bg-primary text-white text-sm">
      <div className="mx-auto max-w-5xl px-4 py-2 flex items-center gap-3">
        <span aria-hidden className="inline-block h-2 w-2 rounded-full bg-accent shrink-0" />
        <p className="leading-snug">
          VASP is running in the{' '}
          <strong className="font-semibold">Capital Markets Authority regulatory sandbox</strong>.
          Protections differ from a fully licensed service.{' '}
          <a href="/disclosures" className="underline underline-offset-2 hover:text-accent">
            Read the risk disclosure
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * The tier badge. Always visible in the top bar, and it opens the tier explainer.
 *
 * A participant should never have to guess why an action is unavailable to them.
 */
export function TierBadge({ tier }: { tier: Tier }) {
  const label: Record<Tier, string> = {
    T0: 'Unverified',
    T1: 'Tier 1',
    T2: 'Tier 2',
    T3: 'Business',
  };

  // T0 is amber, not red. The participant has done nothing wrong: they have simply not finished
  // verifying yet, and colouring that as an error is both untrue and discouraging.
  const tone: Record<Tier, string> = {
    T0: 'bg-warning/10 text-warning border-warning/30',
    T1: 'bg-info/10 text-info border-info/30',
    T2: 'bg-accent/10 text-accent border-accent/30',
    T3: 'bg-primary/10 text-brand border-primary/30',
  };

  return (
    <span
      className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-medium ${tone[tier]}`}
      title={`Your verification level: ${label[tier]}`}
    >
      {label[tier]}
    </span>
  );
}

/*
 * THE GATE LIVES IN `gate.tsx`, not here.
 *
 * There used to be a `Gate` in this file that took `what`, `why`, `action` and `href` as props —
 * which meant every caller wrote its own answer to "which tier unlocks this and where do you go to
 * get it". Four callers, four wordings, and eventually one of them would be wrong about the rules the
 * backend actually enforces.
 *
 * `TierGate` asks for a TIER and works the rest out from the ladder, so there is one answer.
 */

/**
 * The blocked-action notice.
 *
 * This is what a participant sees when the platform refuses to move their money, and it is the most
 * important message in the product. It shows the DISCLOSABLE reason, which the backend deliberately
 * separates from the internal one: the risk officer sees "velocity: 14 movements in the last hour",
 * the participant sees that a security check is under way.
 *
 * Telling a fraudster precisely which rule caught them is telling them how to avoid it next time.
 */
export function BlockedNotice({
  reason,
  stage,
  onDismiss,
}: {
  reason: string;
  stage?: string;
  onDismiss?: () => void;
}) {
  return (
    <div role="alert" className="rounded-card border border-danger/30 bg-danger/5 p-4 text-sm">
      <div className="flex items-start gap-3">
        <svg
          className="mt-0.5 h-5 w-5 shrink-0 text-danger"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 9v2m0 4h.01M5 19h14a2 2 0 001.84-2.75L13.74 4a2 2 0 00-3.48 0L3.16 16.25A2 2 0 005 19z"
          />
        </svg>

        <div className="flex-1">
          <p className="font-medium text-danger">This did not go through</p>
          <p className="mt-1 text-ink">{reason}</p>

          {/* Reassurance first. A participant whose transfer failed wants to know their money is
              safe before they want to know why. */}
          <p className="mt-2 text-ink-muted">Nothing has moved and your balance is unchanged.</p>

          {stage ? (
            <p className="mt-2 font-mono text-xs text-ink-muted">Stopped at: {stage}</p>
          ) : null}
        </div>

        {onDismiss ? (
          <button
            onClick={onDismiss}
            className="text-ink-muted hover:text-ink"
            aria-label="Dismiss"
          >
            &times;
          </button>
        ) : null}
      </div>
    </div>
  );
}

/**
 * A money figure.
 *
 * Tabular, so a column of them aligns and can actually be scanned. The amount arrives from the API
 * as an exact decimal STRING, never a JSON number, and it is never parsed into one here: a float
 * cannot represent 0.1, and this is the number the participant is trusting us with.
 */
export function Money({
  amount,
  asset,
  size = 'md',
}: {
  amount: string;
  asset: string;
  size?: 'sm' | 'md' | 'lg';
}) {
  const cls = {
    sm: 'text-sm',
    md: 'text-lg',
    lg: 'text-3xl',
  }[size];

  return (
    <span className={`tabular font-medium ${cls}`}>
      <span className="text-ink-muted mr-1 text-[0.75em] font-normal">{asset}</span>
      {amount}
    </span>
  );
}
